import { requestV3 } from 'Features/Auth/axios-client'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  InventoryResult,
  InventorySummary,
} from 'Features/Projects/Scenarios/Inventory/Inventory.types'

const fetchInventorySummary = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/inventory-site-summary?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useInventorySummary = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<InventorySummary[]> => {
  return useQuery({
    queryKey: ['inventory-site-summary', projectId, scenarioId],
    queryFn: () => fetchInventorySummary(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

const fetchInventoryResults = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/inventory-site-results?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useInventoryResults = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<InventoryResult[]> => {
  return useQuery({
    queryKey: ['inventory-site-results', projectId, scenarioId],
    queryFn: () => fetchInventoryResults(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

export type InventoryPayloadType = {
  siteKey: number
}

const calculateSafetyStock = async ({
  projectId,
  scenarioId,
  payload,
}: {
  projectId: string
  scenarioId: string
  payload: InventoryPayloadType[]
}) => {
  const { data } = await requestV3({
    url: `/inventory/safety-stock/calculate?projectId=${projectId}&scenarioId=${scenarioId}`,
    method: 'PUT',
    data: payload,
  })
  return data
}

export const useCalculateSafetyStock = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: calculateSafetyStock,
    onSuccess: async (_, { projectId, scenarioId }) => {
      await queryClient.invalidateQueries({
        queryKey: ['replenishment-site-summary', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}
